import { getAbsoluteUrl } from "$lib/utils/urls.js";
import { getDescriptionName } from "$utils/geoNames.js";
import { setContext, getContext, onMount } from "svelte";
import { parseDate, formatDate } from "$lib/utils/dates.js";
import { GEO_LEVELS, FREQUENCIES, SERIES_ORDER, UNIT_TYPES } from "$utils/consts.js";
import { csvParse } from "d3-dsv";
import { formatValue } from "$utils/formats.js";
import { idMap, getCdAbb } from "$utils/geoNames.js";
import metadata from "$data/meta.json";
import labels from "$data/labels.json";

/** @typedef {import("$utils/types.d.ts").GeoLevel} GeoLevel */
/** @typedef {import("$utils/types.d.ts").Frequency} Frequency */
/** @typedef {import("$utils/types.d.ts").DollarType} DollarType */
/** @typedef {import("$utils/types.d.ts").MetricMeta} MetricMeta */
/** @typedef {import("$utils/types.d.ts").TimeSeriesPoint} TimeSeriesPoint */
/** @typedef {import("$utils/types.d.ts").TimeSeriesLayer} TimeSeriesLayer */
/** @typedef {import("$utils/types.d.ts").DataPoint} DataPoint */
/** @typedef {import("$utils/types.d.ts").Series} Series */
/** @typedef {import("$utils/types.d.ts").Metric} Metric */
/** @typedef {import("$utils/types.d.ts").SeriesOverview} SeriesOverview */

export class Data {
  labels = labels;
  /** @type {HTMLElement | undefined} */
  toolContainerEl = $state();
  /** @type {Array<DataPoint> | undefined} */
  nationalData = $state();
  /** @type {Array<DataPoint> | undefined} */
  stateData = $state();
  /** @type {Array<DataPoint> | undefined} */
  districtData = $state();
  dataReady = $derived(this.isReady());
  seriesList = $derived(this.getSeriesList());
  /** @type {Metric} */
  currentMetric = $state("grocery_costs");
  /** @type {string | undefined} */
  currentDistrict = $state();
  /** @type {string} */
  currentState = $state("00");
  /** @type {GeoLevel} */
  currentLevel = $state("state");
  /** @type {DollarType} */
  currentDollarType = $state("nominal");
  currentNationalData = $derived.by(() =>
    this.getTimeSeries(GEO_LEVELS.NATIONAL, this.currentMetric, this.currentDollarType)
  );
  currentChartData = $derived.by(() => this.getChartData());
  currentMapData = $derived.by(() => this.getMapData());
  currentMetadata = $derived.by(() => this.getMetadata(this.currentLevel, this.currentMetric));
  stateDropdownList = $derived.by(() => {
    return Array.from(idMap.values())
      .map(({ geoid, state_name }) => ({
        value: geoid,
        label: state_name
      }))
      .toSorted((a, b) => {
        if (b.label === "US") {
          return 1;
        }
        return a.label.localeCompare(b.label);
      });
  });
  districtDropdownList = $derived.by(() => this.getDistrictList());
  seriesOverviews = $derived.by(() => this.getAllOverviews());
  levelMap = this.getLevelMap(metadata);
  mostRecentDate = $derived.by(() => {
    return this.getMostRecentDate();
  });
  isCurrentMetricDollar = $derived.by(() => this.isDollarSeries(this.currentMetric));

  constructor() {
    onMount(() => {
      this.fetchData("national").then((data) => {
        this.nationalData = data;
      });
      this.fetchData("state").then((data) => {
        this.stateData = data;
      });
      this.fetchData("cd").then((data) => {
        this.districtData = data;
      });
    });

    $effect(() => {
      //if level is state, set currentDistrict to none
      if (this.currentLevel === GEO_LEVELS.STATE) {
        this.currentDistrict = undefined;
      }
    });

    // if user changes state while district is selected, unset district
    $effect(() => {
      if (this.currentDistrict && this.currentState !== this.currentDistrict.slice(0, 2)) {
        this.currentDistrict = undefined;
      }
    });

    // if user selects a metric without district data, make sure states are active
    $effect(() => {
      if (
        this.currentLevel === GEO_LEVELS.DISTRICT &&
        !this.levelMap.get(this.currentMetric)?.has(GEO_LEVELS.DISTRICT)
      ) {
        this.currentLevel = GEO_LEVELS.STATE;
      }
    });

    // if user has selected a district, but that district doesn't exist in the current chart data, clear the selected district
    $effect(() => {
      let geoExists = false;
      if (this.currentDistrict && this.districtData) {
        let availableDistricts = new Set();
        for (let layer of this.currentChartData) {
          if (layer.level === GEO_LEVELS.DISTRICT) {
            // check if geoid exists in any data point
            geoExists = layer.data.some((d) => Object.hasOwn(d, this.currentDistrict));
          }
        }
      }
      if (!geoExists) {
        this.currentDistrict = undefined;
      }
    });
  }

  /** Fetch data from a given URL
   * @param {string} level - the level of data to fetch (national, state, cd)
   * @returns {Promise<Array<DataPoint>>} - a promise that resolves to the fetched data
   */
  async fetchData(level) {
    const dataUrl = getAbsoluteUrl(`/${level}_data.csv`);
    const resp = await fetch(dataUrl);
    const text = await resp.text();
    const parsed = csvParse(text, this.parseCsvRow.bind(this));
    return parsed;
  }

  /** CheckY if all data is loaded and ready
   * @returns {boolean} - true if all data is loaded
   */
  isReady() {
    let isReady =
      this.nationalData &&
      this.nationalData.length > 0 &&
      this.stateData &&
      this.stateData.length > 0 &&
      this.districtData &&
      this.districtData.length > 0;
    return isReady || false;
  }

  /**
   * Get unique geoids from a dataset
   * @param {Array<DataPoint>} dataset - The dataset to extract geoids from
   * @returns {Set<string>} - Set of unique geoids
   */
  getUniqueGeoids(dataset) {
    if (!dataset || dataset.length === 0) return new Set();
    return new Set(dataset.map((item) => item.geoid));
  }

  /**
   * Format raw time series data into an array of level objects
   * Each level contains its own array of date objects with geoid keys
   * @param {GeoLevel} level - the level of data (national, state, cd)
   * @param {Metric} series - the series to use
   * @param {boolean} stateFilter - whether or not to filter out out of state geoids
   * @returns {Array<TimeSeriesLayer>} - array of level objects with time series data
   */
  getTimeSeries(level, series, stateFilter) {
    // Determine if this is a dollar series and get the correct series name
    const isDollarSeries = this.isDollarSeries(series);
    const seriesName = isDollarSeries ? `${series}_${this.currentDollarType}` : series;

    // Check if data is ready
    if (!this.dataReady) {
      return [];
    }

    const result = [];

    // Helper function to merge data by date for a given dataset and set of geoids
    const mergeByDate = (dataArray, geoidsToInclude) => {
      const dateMap = new Map();

      for (const item of dataArray) {
        // Skip if geoid not in include list
        if (!geoidsToInclude.has(item.geoid)) continue;
        // Skip if value is null
        if (item[seriesName] === null) continue;

        const dateStr = formatDate(item.date);
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, {
            date: item.date,
            series: seriesName
          });
        }
        dateMap.get(dateStr)[item.geoid] = item[seriesName];
      }

      // Convert map to sorted array
      return Array.from(dateMap.values()).sort((a, b) => a.date - b.date);
    };

    // Build the result based on the requested level
    if (level === GEO_LEVELS.NATIONAL) {
      // For national level, only include national data
      const nationalData = mergeByDate(this.nationalData, new Set(["00"]));
      if (nationalData.length > 0) {
        result.push({
          level: "national",
          data: nationalData
        });
      }
    } else if (level === GEO_LEVELS.STATE) {
      // For state level, include states and national for comparison

      // Add state data
      let stateGeoids;
      if (stateFilter && this.currentState && this.currentState !== "00") {
        // If filtering, only include current state
        stateGeoids = new Set([this.currentState]);
      } else {
        // Otherwise include all states (except national "00")
        stateGeoids = new Set(
          Array.from(this.getUniqueGeoids(this.stateData || [])).filter((geoid) => geoid !== "00")
        );
      }

      const stateData = mergeByDate(this.stateData, stateGeoids);
      if (stateData.length > 0) {
        result.push({
          level: "state",
          data: stateData
        });
      }

      // Add national data for comparison
      const nationalData = mergeByDate(this.nationalData, new Set(["00"]));
      if (nationalData.length > 0) {
        result.push({
          level: "national",
          data: nationalData
        });
      }
    } else if (level === GEO_LEVELS.DISTRICT) {
      // For district level, include districts, current state, and national

      // Add district data
      const districtGeoids = new Set();
      for (const item of this.districtData || []) {
        // Apply state filter if requested
        if (stateFilter && this.currentState && !item.geoid.startsWith(this.currentState)) {
          continue;
        }
        districtGeoids.add(item.geoid);
      }

      const districtData = mergeByDate(this.districtData, districtGeoids);
      if (districtData.length > 0) {
        result.push({
          level: "district",
          data: districtData
        });
      }

      // Add state data (only current state if selected)
      if (this.currentState && this.currentState !== "00") {
        const stateData = mergeByDate(this.stateData, new Set([this.currentState]));
        if (stateData.length > 0) {
          result.push({
            level: "state",
            data: stateData
          });
        }
      }

      // Add national data for comparison
      const nationalData = mergeByDate(this.nationalData, new Set(["00"]));
      if (nationalData.length > 0) {
        result.push({
          level: "national",
          data: nationalData
        });
      }
    }

    return result;
  }

  /**
   * Get chart data for the current selections
   * @returns {Array<TimeSeriesLayer>} - Array of level objects with time series data
   */
  getChartData() {
    // For district level, apply state filter to only show districts from current state
    // For other levels, don't filter (or use current filter settings)
    const shouldFilter = this.currentLevel === "district";
    return this.getTimeSeries(this.currentLevel, this.currentMetric, shouldFilter);
  }

  /**
   * Get map data for the current selections
   * Maps need all geographies at a single point in time in a flat structure
   * @returns {TimeSeriesPoint} - Object with geoid keys and values for the most recent date
   */
  getMapData() {
    // Get all series for the current level (no state filter for maps)
    const levelArrays = this.getTimeSeries(this.currentLevel, this.currentMetric, false);

    // Find the data for the current level specifically
    const currentLevelData = levelArrays.find(
      (obj) => obj.level === this.currentLevel.replace("cd", "district")
    );

    if (!currentLevelData || currentLevelData.data.length === 0) {
      return { date: new Date(), series: this.currentMetric };
    }

    // Get the most recent entry from the current level's data
    const mostRecentEntry = currentLevelData.data[currentLevelData.data.length - 1];

    // Build the return object with all geoid values from the most recent date
    const mapData = {
      date: mostRecentEntry.date,
      series: this.currentMetric
    };

    // Copy all geoid values to mapData
    for (const [key, value] of Object.entries(mostRecentEntry)) {
      // Skip metadata keys
      if (key === "date" || key === "series" || key === "level") continue;
      mapData[key] = value;
    }

    return mapData;
  }

  /**
   * Get series overview for a specific series
   * Used for the series overview cards/list
   * @param {Metric} series - The series name
   * @returns {SeriesOverview | undefined} - Overview object with metadata and recent value
   */
  getSeriesOverview(series) {
    const timeSeries = this.getTimeSeries(GEO_LEVELS.NATIONAL, series, false);
    const seriesMeta = this.getMetadata(GEO_LEVELS.NATIONAL, series);
    if (!seriesMeta) {
      return undefined;
    }

    // Get the national level data
    const nationalLevel = timeSeries.find((level) => level.level === "national");
    if (!nationalLevel || nationalLevel.data.length === 0) {
      return {
        series,
        label: series,
        units: seriesMeta.units,
        timeSeries: [],
        category: seriesMeta.category,
        recentValue: null
      };
    }

    const recentEntry = nationalLevel.data[nationalLevel.data.length - 1];

    return {
      series,
      label: seriesMeta.long_label,
      units: seriesMeta.units,
      timeSeries: nationalLevel.data,
      category: seriesMeta.category,
      recentValue: recentEntry["00"]
    };
  }

  /**
   * Get overviews for all series, ordered by category
   * @returns {Array<SeriesOverview | undefined>} - Array of series overview objects
   */
  getAllOverviews() {
    // return overview for all series, ordered by category
    return this.seriesList
      .map((series) => this.getSeriesOverview(series))
      .toSorted((a, b) => {
        if (!a || !b) return 0;
        return a.category.localeCompare(b.category);
      });
  }

  /** Format a date object into a string for display or storage
   * @param {Date} realDate - the date object to format
   * @returns {string} - the formatted date string
   */
  formatDate(realDate) {
    return formatDate(realDate);
  }

  /** Parse a date string into a date object
   * @param {string} strDate - the date string to parse
   * @returns {Date | null} - the parsed date object
   */
  parseDate(strDate) {
    return parseDate(strDate, FREQUENCIES.INPUT);
  }

  /**
   * Get metadata for a given level and series
   * @param { string } level - the level of data (national, state, cd)
   * @param { string } series - the series name to fetch
   * @returns { MetricMeta | undefined } - the metadata object or null if not found
   */
  getMetadata(level, series) {
    let meta = metadata.find((d) => d.series == series && d.geography === level);
    return meta;
  }

  /**
   *@returns {Array<Metric>} - list of all unique series names from metadata
   */
  getSeriesList() {
    return SERIES_ORDER;
  }

  /**
   * Check if a series is a dollar type series
   * @param {Metric} series - the series name to check
   * @returns {boolean} - true if the series is a dollar type, false otherwise
   */
  isDollarSeries(series) {
    let meta = this.getMetadata(GEO_LEVELS.NATIONAL, series);
    return meta ? meta.units === "dollars" : false;
  }

  setDistrict(geoid) {
    this.currentDistrict = geoid;
  }

  setLevel(level) {
    this.currentLevel = level;
  }

  /**
   * Parse a CSV row into a structured object
   * @param {Object<string, string>} row - the CSV row object
   * @returns {DataPoint} - the parsed row object
   */
  parseCsvRow(row) {
    let skip_cols = ["geoid", "date", "level"];
    let result = {
      geoid: row.geoid,
      date: this.parseDate(row.date),
      level: row.level
    };
    for (let col in row) {
      if (skip_cols.includes(col)) continue;
      let val = Number(row[col]);
      result[col] = isNaN(val) ? null : val;
    }
    return result;
  }

  /** Get a list of districts
   * @returns {Array<{value: string, label: string}>} - list of district options
   */
  getDistrictList() {
    if (!this.districtData) return [];
    return Array.from(new Set(this.districtData.map(({ geoid }) => geoid))).map((geoid) => ({
      value: geoid,
      label: getCdAbb(geoid)
    }));
  }

  /**
   * Retrieve necessary data points for a metric/geo combo's descriptive text
   * @param { GeoLevel } level
   * @param { string } geoid
   * @param { Metric } metric
   * @returns { Record<string, string | null> } - key-value pairs of metric variables and their values
   */
  getDescriptionData(level, geoid, metric) {
    if (!this.dataReady) {
      return {};
    }
    // sometimes current level is "state", but the active geo is really the national number. If currentGeo is national, set level correctly, otherwise use as-is. Likewise, sometimes the level is "district", but only the state is selected
    let realLevel = geoid === "00" ? GEO_LEVELS.NATIONAL : level;
    let realGeoid = geoid;
    if (level === GEO_LEVELS.DISTRICT && !this.currentDistrict) {
      realLevel = GEO_LEVELS.STATE;
      realGeoid = this.currentState;
    }
    const metricInfo = this.getMetadata(realLevel, metric);
    const metricData = this.getTimeSeries(realLevel, metric, false);
    let levelSeries = metricData.find((d) => d.level === realLevel);
    if (!levelSeries) {
      return {};
    }

    let recentValue = null;
    let recentDate = null;
    // if recentValue is null, go through each in time series to find non-null
    if (recentValue === null) {
      for (let i = levelSeries.data.length - 1; i >= 0; i--) {
        if (levelSeries.data[i][geoid] !== null) {
          recentValue = levelSeries.data[i][geoid];
          recentDate = levelSeries.data[i].date;
          break;
        }
      }
    }

    // find comparison points by searching back through the time series to find the next non-null data piont
    let firstPoint = metricInfo
      ? this.findComparisonDataPoint(levelSeries.data, metricInfo.frequency)
      : null;
    let firstDate = firstPoint?.date || null;
    let firstValue = firstPoint?.[geoid] || null;
    // for (let i = levelSeries.data.length - 2; i >= 0; i--) {
    //   if (levelSeries.data[i][geoid] !== null) {
    //     firstValue = levelSeries.data[i][geoid];
    //     firstDate = levelSeries.data[i].date;
    //     break;
    //   }
    // }

    const pctChange =
      firstValue && recentValue ? Math.abs((recentValue - firstValue) / firstValue) : null;

    return {
      currentMetric: metricInfo.long_label.toLowerCase(),
      currentValue: formatValue(recentValue, metricInfo?.units),
      currentDate: formatDate(recentDate, metricInfo?.frequency),
      firstValue: firstValue ? formatValue(firstValue, metricInfo?.units) : null,
      trendDirection: firstValue > recentValue ? "down" : "up",
      pctChange: formatValue(pctChange, UNIT_TYPES.RATE_CHANGE),
      currentGeo: getDescriptionName(geoid),
      firstDate: formatDate(firstDate, metricInfo?.frequency),
      descriptionTemplate: metricInfo?.description_template || null,
      headerTemplate: metricInfo?.header_template || null
    };
  }

  /**
   * Create a map of available levels for each series from metadata
   * @param {Array<MetricMeta>} meta - the metadata array
   * @returns {Map<Series, Set<GeoLevel>>} - map of series to available levels
   */
  getLevelMap(meta) {
    const levelMap = new Map();
    for (const { series, geography } of meta) {
      if (!levelMap.has(series)) {
        levelMap.set(series, new Set());
      }
      levelMap.get(series).add(geography);
    }
    return levelMap;
  }
  /**
   * Retrieve necessary data points for a metric/geo combo's descriptive text
   * @param { GeoLevel } level
   * @param { string } geoid
   * @param { Metric } metric
   * @returns { Record<string, string | null> } - key-value pairs of metric variables and their values
   */
  getMapDescriptionData(level, geoid, metric) {
    if (!this.dataReady) {
      return {};
    }
    // sometimes current level is "state", but the active geo is really the national number. If currentGeo is national, set level correctly, otherwise use as-is. Likewise, sometimes the level is "district", but only the state is selected
    let realLevel = geoid === "00" ? GEO_LEVELS.NATIONAL : level;
    let realGeoid = geoid;
    if (level === GEO_LEVELS.DISTRICT && !this.currentDistrict) {
      realLevel = GEO_LEVELS.STATE;
      realGeoid = this.currentState;
    }
    const metricInfo = this.getMetadata(realLevel, metric);
    const metricData = this.getTimeSeries(realLevel, metric, false);
    let levelSeries = metricData.find((d) => d.level === realLevel);
    if (!levelSeries) {
      return {};
    }

    let recentValue = null;
    let recentDate = null;
    // if recentValue is null, go through each in time series to find non-null
    if (recentValue === null) {
      for (let i = levelSeries.data.length - 1; i >= 0; i--) {
        if (levelSeries.data[i][geoid] !== null) {
          recentValue = levelSeries.data[i][geoid];
          recentDate = levelSeries.data[i].date;
          break;
        }
      }
    }

    // find comparison points by searching back through the time series to find the next non-null data piont
    let nationalSeries = metricData.find((d) => d.level === GEO_LEVELS.NATIONAL);
    let nationalPoint = nationalSeries.data[nationalSeries.data.length - 1];
    let nationalValue = nationalPoint?.["00"] || null;

    return {
      currentMetric: metricInfo.long_label.toLowerCase(),
      currentValue: formatValue(recentValue, metricInfo?.units),
      currentDate: formatDate(recentDate, metricInfo?.frequency),
      nationalValue:
        nationalValue && realLevel !== GEO_LEVELS.NATIONAL
          ? formatValue(nationalValue, metricInfo?.units)
          : null,
      currentGeo: getDescriptionName(geoid),
      descriptionTemplate: metricInfo?.description_template || null,
      headerTemplate: metricInfo?.header_template || null
    };
  }

  /**
   * Finds the comparison data point for the most recent point in a time series.
   *
   * For annual data: returns the previous year's data point
   * For sub-annual data: returns the first data point of the current year,
   *   unless the current point is already the first of its year, in which case
   *   it returns the first point of the previous year
   *
   * @param {TimeSeriesPoint[]} timeSeries - Array of time series data points
   * @param {Frequency} frequency - The frequency of the data
   * @returns {TimeSeriesPoint | null} The comparison data point, or null if not found
   */
  findComparisonDataPoint(timeSeries, frequency) {
    if (!timeSeries || timeSeries.length === 0) {
      return null;
    }

    // Sort by date ascending to ensure we have the correct order
    const sortedSeries = timeSeries.toSorted((a, b) => a.date.getTime() - b.date.getTime());

    const mostRecent = sortedSeries[sortedSeries.length - 1];
    const mostRecentYear = mostRecent.date.getFullYear();

    // For annual data, find the previous year
    if (frequency === FREQUENCIES.ANNUAL) {
      const targetYear = mostRecentYear - 1;
      return (
        sortedSeries.find((point) => {
          return point.date.getFullYear() === targetYear;
        }) || null
      );
    }

    // For sub-annual data
    if (
      [FREQUENCIES.MONTHLY, FREQUENCIES.WEEKLY, FREQUENCIES.QUARTERLY, FREQUENCIES.OTHER].includes(
        frequency
      )
    ) {
      // Find the first data point in the current year
      const firstPointInCurrentYear = sortedSeries.find((point) => {
        return point.date.getFullYear() === mostRecentYear;
      });

      // Check if the most recent point is the first point of its year
      const isFirstOfYear =
        firstPointInCurrentYear &&
        mostRecent.date.getTime() === firstPointInCurrentYear.date.getTime();

      if (isFirstOfYear) {
        // If current is first of year, get first of previous year
        const targetYear = mostRecentYear - 1;
        return (
          sortedSeries.find((point) => {
            return point.date.getFullYear() === targetYear;
          }) || null
        );
      } else {
        // Otherwise, return the first point of current year
        return firstPointInCurrentYear;
      }
    }

    // For other frequencies, return the first value or null
    return timeSeries[0] || null;
  }

  getMostRecentDate() {
    if (!this.dataReady) return null;
    // find the most recent date across all datasets
    let recentDates = [];
    if (this.nationalData && this.nationalData.length > 0) {
      let nationalDates = this.nationalData.map((d) => d.date);
      recentDates.push(new Date(Math.max(...nationalDates.map((d) => d.getTime()))));
    }
    if (this.stateData && this.stateData.length > 0) {
      let stateDates = this.stateData.map((d) => d.date);
      recentDates.push(new Date(Math.max(...stateDates.map((d) => d.getTime()))));
    }
    if (this.districtData && this.districtData.length > 0) {
      let districtDates = this.districtData.map((d) => d.date);
      recentDates.push(new Date(Math.max(...districtDates.map((d) => d.getTime()))));
    }
    if (recentDates.length === 0) return null;
    let mostRecent = Math.max(...recentDates.map((d) => d.getTime()));
    return new Date(mostRecent);
  }
}

const contextKey = Symbol("dataContext");

/** @returns {Data} */
export function createData() {
  const data = new Data();
  setContext(contextKey, data);
  return data;
}

/** @returns {Data} */
export function useData() {
  return getContext(contextKey);
}
